import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import { useFormValidation } from 'Hooks/useFormValidation';
import { ROUTES } from 'Utils/routes';
import { ForgotPasswordFormErrors } from 'Types/forms';
import Copyright from 'Components/Copyright/Copyright';
import FormTextField from 'Components/FormTextField/FormTextField';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { values, errors, setFieldValue, clearFieldError, validateEmail } =
    useFormValidation<ForgotPasswordFormErrors>({ email: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateEmail('email')) {
      return;
    }

    setIsLoading(true);
    const result = await forgotPassword(values.email);
    setIsLoading(false);

    if (result.success) {
      navigate(ROUTES.CONFIRM_FORGOT_PASSWORD, { replace: true });
    } else {
      showNotification(result.message);
    }
  };

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.dark' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          Forgot password?
        </Typography>
        <Typography variant='body2'>Reset password in two quick steps</Typography>
        <Box component='form' noValidate onSubmit={handleSubmit} sx={{ mt: 3, width: '100%' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormTextField
                required
                name='email'
                label='Email Address'
                autoComplete='email'
                value={values.email}
                fieldError={errors.email}
                disabled={isLoading}
                onFieldChange={setFieldValue}
                onClearError={clearFieldError}
              />
            </Grid>
          </Grid>
          <Button
            color='secondary'
            type='submit'
            fullWidth
            variant='contained'
            disabled={isLoading}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Next'}
          </Button>
          <Grid container justifyContent='flex-end'>
            <Grid item>
              <Link href={ROUTES.SIGN_IN} variant='body2' color='secondary.contrastText'>
                Remember password? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}
